<template>
  <div ref="mapContainer" class="vue-map-container" />
</template>

<script lang="ts">
import { subscribeGoogleMapsReady } from '@/lib/googleMaps';

type LatLng = {
  lat: number;
  lng: number;
};

export default {
  name: 'GoogleMapWithCircle',
  props: {
    center: {
      type: Object as () => LatLng,
      required: true,
    },
    zoom: {
      type: Number,
      default: 9,
    },
    mapTypeId: {
      type: String,
      default: 'roadmap',
    },
    radius: {
      type: Number,
      required: true,
    },
    mapOptions: {
      type: Object,
      default: () => ({}),
    },
    circleOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  data(): {
    map: any;
    circle: any;
    isActive: boolean;
    unsubscribeGoogleMaps: (() => void) | null;
  } {
    return {
      map: null,
      circle: null,
      isActive: true,
      unsubscribeGoogleMaps: null,
    };
  },
  watch: {
    center: {
      deep: true,
      handler() {
        this.syncMapOverlay();
      },
    },
    radius() {
      this.syncMapOverlay();
    },
    zoom(newZoom: number) {
      this.map?.setZoom(newZoom);
    },
  },
  mounted() {
    this.unsubscribeGoogleMaps = subscribeGoogleMapsReady((ready) => {
      if (ready) {
        this.initializeMap();
      }
    });
  },
  beforeUnmount() {
    this.isActive = false;
    this.unsubscribeGoogleMaps?.();
    this.unsubscribeGoogleMaps = null;
    this.circle?.setMap(null);
    this.circle = null;
    this.map = null;
  },
  methods: {
    initializeMap() {
      if (!this.isActive || this.map) {
        return;
      }

      const container = this.$refs.mapContainer as HTMLElement | undefined;
      if (!container || typeof window.google?.maps?.Map !== 'function') {
        return;
      }

      const center = this.normalizedCenter();
      this.map = new window.google.maps.Map(container, {
        center,
        zoom: this.zoom,
        mapTypeId: this.mapTypeId,
        ...this.mapOptions,
      });
      this.circle = new window.google.maps.Circle({
        map: this.map,
        center,
        radius: this.radius,
        ...this.circleOptions,
      });
    },
    normalizedCenter(): LatLng {
      return { lat: this.center.lat, lng: this.center.lng };
    },
    syncMapOverlay() {
      if (!this.map || !this.circle) {
        return;
      }

      const center = this.normalizedCenter();
      this.map.setCenter(center);
      this.circle.setCenter(center);
      this.circle.setRadius(this.radius);
    },
  },
};
</script>
